import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  Alert,
  LinearProgress,
} from "@mui/material";

import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../components/firebase";

import {
  collection,
  deleteDoc,
  doc,
  getDocs,
} from "firebase/firestore";

// ✅ ВСТАВКА СТАРОГО PUBLISHER ВМЕСТО ВАШЕЙ ФОРМЫ
import AdminMarketPublish from "../components/AdminMarketPublish";

// (опционально) если хочешь тут же показать общий Market feed (streams+preds) — можно использовать
// import { useMarketFeed } from "../hooks/useMarketFeed";

/* ======================
   UI TOKENS (темный UI)
   ====================== */
const UI = {
  pageBg:
    "radial-gradient(1200px circle at 20% 0%, rgba(56,189,248,.22), transparent 45%), radial-gradient(900px circle at 90% 10%, rgba(34,197,94,.14), transparent 40%), linear-gradient(180deg, #020617 0%, #020617 40%, #040b1f 100%)",
  glass:
    "linear-gradient(135deg, rgba(18,22,28,.86) 0%, rgba(10,13,17,.86) 100%)",
  border: "1px solid rgba(255,255,255,.10)",
  borderSoft: "1px solid rgba(255,255,255,.08)",
  text: "#e5e7eb",
  subtext: "#9ca3af",
  danger: "#fb7185",
  ok: "#34d399",
  accent: "#38bdf8",
};

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ mt: 2 }}>{children}</Box>;
}

/* ======================
   Main Component
   ====================== */
export default function AdminPanelPro() {
  const [user, loadingAuth] = useAuthState(auth);
  const [isAdmin, setIsAdmin] = useState(false);

  const [tab, setTab] = useState(0);

  // data
  const [users, setUsers] = useState([]);
  const [promos, setPromos] = useState([]);
  const [queue, setQueue] = useState([]);

  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingPromos, setLoadingPromos] = useState(true);
  const [loadingQueue, setLoadingQueue] = useState(true);

  // admin check
  useEffect(() => {
    let alive = true;

    (async () => {
      if (!user) {
        if (alive) setIsAdmin(false);
        return;
      }
      try {
        const token = await user.getIdTokenResult(true);
        const role = token?.claims?.role || null;
        if (alive) setIsAdmin(role === "admin");
      } catch {
        if (alive) setIsAdmin(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [user]);

  const refetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const snap = await getDocs(collection(db, "users"));
      setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const refetchPromos = useCallback(async () => {
    setLoadingPromos(true);
    try {
      const snap = await getDocs(collection(db, "promos"));
      setPromos(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } finally {
      setLoadingPromos(false);
    }
  }, []);

  const refetchQueue = useCallback(async () => {
    setLoadingQueue(true);
    try {
      const snap = await getDocs(collection(db, "moderationQueue"));
      setQueue(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } finally {
      setLoadingQueue(false);
    }
  }, []);

  // initial fetch
  useEffect(() => {
    if (!isAdmin) return;
    refetchUsers();
    refetchPromos();
    refetchQueue();
  }, [isAdmin, refetchUsers, refetchPromos, refetchQueue]);

  const onDeleteStream = async (streamId) => {
    if (!window.confirm("Delete this stream document?")) return;
    try {
      await deleteDoc(doc(db, "avatar_streams", streamId));
    } catch (e) {
      console.error(e);
      alert(e?.message || "Delete failed");
    }
  };

  const approvePromo = async (promoId, statusValue) => {
    try {
      // оставил как у тебя: мгновенный апдейт локального списка
      const { updateDoc } = await import("firebase/firestore");
      await updateDoc(doc(db, "promos", promoId), { status: statusValue });
      setPromos((prev) => prev.map((p) => (p.id === promoId ? { ...p, status: statusValue } : p)));
    } catch (e) {
      console.error(e);
      alert(e?.message || "Update promo failed");
    }
  };

  const handleModeration = async (item, action) => {
    try {
      const { updateDoc } = await import("firebase/firestore");

      await updateDoc(doc(db, "promos", item.promoId), {
        status: action,
        moderation: {
          autoChecked: false,
          reasons: action === "approved" ? [] : item.reasons || [],
          aiModeration: {
            passed: action === "approved",
            reason: action === "approved" ? "Manually approved" : "Manually rejected",
          },
          checkedAt: new Date(),
        },
      });

      await deleteDoc(doc(db, "moderationQueue", item.id));
      await refetchQueue();
      await refetchPromos();
    } catch (e) {
      console.error(e);
      alert(e?.message || "Moderation update failed");
    }
  };

  if (loadingAuth) {
    return (
      <Box sx={{ minHeight: "100vh", background: UI.pageBg, p: 3 }}>
        <Typography sx={{ color: UI.text }}>Loading...</Typography>
      </Box>
    );
  }

  if (!user || !isAdmin) {
    return (
      <Box sx={{ minHeight: "100vh", background: UI.pageBg, p: 3 }}>
        <Card elevation={0} sx={{ background: UI.glass, border: UI.border, borderRadius: 3, maxWidth: 560 }}>
          <CardContent>
            <Typography sx={{ color: UI.text, fontWeight: 950, fontSize: 18 }}>Access denied</Typography>
            <Typography sx={{ color: UI.subtext, mt: 1 }}>
              Admin only. Проверь custom claims <b>role=admin</b> и правила Firestore.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", background: UI.pageBg, p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 2, flexWrap: "wrap" }}>
        <Typography sx={{ color: UI.text, fontWeight: 980, fontSize: 22 }}>
          PromosPay — Admin Console
        </Typography>
        <Chip
          size="small"
          label="admin"
          sx={{
            height: 22,
            fontWeight: 900,
            color: UI.text,
            background: "rgba(56,189,248,.12)",
            border: "1px solid rgba(56,189,248,.30)",
          }}
        />
        <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
          <Tooltip title="Refresh Users/Promos/Queue">
            <IconButton
              onClick={() => {
                refetchUsers();
                refetchPromos();
                refetchQueue();
              }}
              sx={{
                color: UI.text,
                border: UI.borderSoft,
                background: "rgba(2,6,23,.25)",
                "&:hover": { background: "rgba(56,189,248,.10)" },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Tabs card */}
      <Card elevation={0} sx={{ background: UI.glass, border: UI.border, borderRadius: 3 }}>
        <CardContent sx={{ pb: 0.5 }}>
          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 42,
              "& .MuiTab-root": {
                minHeight: 42,
                textTransform: "none",
                fontWeight: 950,
                color: "rgba(203,213,225,.8)",
              },
              "& .Mui-selected": { color: UI.text },
              "& .MuiTabs-indicator": { height: 3, backgroundColor: UI.accent },
            }}
          >
            <Tab label="Streams" />
            <Tab label="Users" />
            <Tab label="Promos" />
            <Tab label="Moderation Queue" />
          </Tabs>
        </CardContent>
      </Card>

      {/* ===== Streams ===== */}
      <TabPanel value={tab} index={0}>
        <Grid container spacing={2}>
          {/* left: ✅ вместо вашей формы — СТАРЫЙ AdminMarketPublish */}
          <Grid item xs={12} lg={7}>
            <AdminMarketPublish />
          </Grid>

          {/* right: Streams Library (оставил как есть — список можно держать отдельно) */}
          <Grid item xs={12} lg={5}>
            <Card elevation={0} sx={{ background: UI.glass, border: UI.border, borderRadius: 3 }}>
              <CardContent>
                <Typography sx={{ color: UI.text, fontWeight: 980, fontSize: 18, mb: 1 }}>
                  Streams Library
                </Typography>

                <Divider sx={{ mb: 2, borderColor: "rgba(255,255,255,.10)" }} />

                <Typography sx={{ color: "rgba(148,163,184,.85)", fontSize: 12, mb: 1.5 }}>
                  Для удаления stream — вводи ID из Firestore (avatar_streams) ниже.
                </Typography>

                <DeleteStreamQuick onDelete={onDeleteStream} UI={UI} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* ===== Users ===== */}
      <TabPanel value={tab} index={1}>
        <Card elevation={0} sx={{ background: UI.glass, border: UI.border, borderRadius: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography sx={{ color: UI.text, fontWeight: 980, fontSize: 18 }}>Users</Typography>
              <Box sx={{ ml: "auto" }}>
                <Chip
                  size="small"
                  label={loadingUsers ? "loading..." : `${users.length} users`}
                  sx={{ color: UI.text, border: UI.borderSoft, background: "rgba(2,6,23,.18)", fontWeight: 900 }}
                />
              </Box>
            </Box>

            <Divider sx={{ mb: 2, borderColor: "rgba(255,255,255,.10)" }} />

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: "rgba(203,213,225,.85)", fontWeight: 900 }}>Email</TableCell>
                  <TableCell sx={{ color: "rgba(203,213,225,.85)", fontWeight: 900 }}>Role</TableCell>
                  <TableCell sx={{ color: "rgba(203,213,225,.85)", fontWeight: 900 }}>Name</TableCell>
                  <TableCell sx={{ color: "rgba(203,213,225,.85)", fontWeight: 900 }}>UID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell sx={{ color: UI.text }}>{u.email || "—"}</TableCell>
                    <TableCell sx={{ color: UI.text }}>{u.role || "—"}</TableCell>
                    <TableCell sx={{ color: UI.text }}>{u.displayName || u.name || "—"}</TableCell>
                    <TableCell sx={{ color: UI.subtext }}>{u.id}</TableCell>
                  </TableRow>
                ))}
                {!loadingUsers && users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ color: UI.subtext }}>
                      No users
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </TabPanel>

      {/* ===== Promos ===== */}
      <TabPanel value={tab} index={2}>
        <Card elevation={0} sx={{ background: UI.glass, border: UI.border, borderRadius: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography sx={{ color: UI.text, fontWeight: 980, fontSize: 18 }}>Promos</Typography>
              <Box sx={{ ml: "auto" }}>
                <Chip
                  size="small"
                  label={loadingPromos ? "loading..." : `${promos.length} items`}
                  sx={{ color: UI.text, border: UI.borderSoft, background: "rgba(2,6,23,.18)", fontWeight: 900 }}
                />
              </Box>
            </Box>

            <Divider sx={{ mb: 2, borderColor: "rgba(255,255,255,.10)" }} />

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: "rgba(203,213,225,.85)", fontWeight: 900 }}>Title</TableCell>
                  <TableCell sx={{ color: "rgba(203,213,225,.85)", fontWeight: 900 }}>Creator</TableCell>
                  <TableCell sx={{ color: "rgba(203,213,225,.85)", fontWeight: 900 }}>Budget</TableCell>
                  <TableCell sx={{ color: "rgba(203,213,225,.85)", fontWeight: 900 }}>Category</TableCell>
                  <TableCell sx={{ color: "rgba(203,213,225,.85)", fontWeight: 900 }}>Token</TableCell>
                  <TableCell sx={{ color: "rgba(203,213,225,.85)", fontWeight: 900 }}>Status</TableCell>
                  <TableCell sx={{ color: "rgba(203,213,225,.85)", fontWeight: 900 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {promos.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell sx={{ color: UI.text }}>{p.title || p.marketTitle || "—"}</TableCell>
                    <TableCell sx={{ color: UI.text }}>{p.creator || p.company || p.creatorHandle || "—"}</TableCell>
                    <TableCell sx={{ color: UI.text }}>{p.budget ?? "—"}</TableCell>
                    <TableCell sx={{ color: UI.text }}>{p.category || "—"}</TableCell>
                    <TableCell sx={{ color: UI.text }}>{p.token || "—"}</TableCell>
                    <TableCell sx={{ color: UI.text }}>{p.status || "—"}</TableCell>
                    <TableCell sx={{ color: UI.text }}>
                      {p.status === "pending" ? (
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => approvePromo(p.id, "approved")}
                            sx={{ color: UI.ok, borderColor: "rgba(52,211,153,.35)" }}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => approvePromo(p.id, "rejected")}
                            sx={{ color: UI.danger, borderColor: "rgba(251,113,133,.35)" }}
                          >
                            Reject
                          </Button>
                        </Box>
                      ) : (
                        <Typography sx={{ color: UI.subtext, fontSize: 12 }}>—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!loadingPromos && promos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ color: UI.subtext }}>
                      No promos
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </TabPanel>

      {/* ===== Moderation Queue ===== */}
      <TabPanel value={tab} index={3}>
        <Card elevation={0} sx={{ background: UI.glass, border: UI.border, borderRadius: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography sx={{ color: UI.text, fontWeight: 980, fontSize: 18 }}>
                Moderation Queue
              </Typography>
              <Box sx={{ ml: "auto" }}>
                <Chip
                  size="small"
                  label={loadingQueue ? "loading..." : `${queue.length} items`}
                  sx={{ color: UI.text, border: UI.borderSoft, background: "rgba(2,6,23,.18)", fontWeight: 900 }}
                />
              </Box>
            </Box>

            <Divider sx={{ mb: 2, borderColor: "rgba(255,255,255,.10)" }} />

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: "rgba(203,213,225,.85)", fontWeight: 900 }}>Promo ID</TableCell>
                  <TableCell sx={{ color: "rgba(203,213,225,.85)", fontWeight: 900 }}>Submitted At</TableCell>
                  <TableCell sx={{ color: "rgba(203,213,225,.85)", fontWeight: 900 }}>Reasons</TableCell>
                  <TableCell sx={{ color: "rgba(203,213,225,.85)", fontWeight: 900 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {queue.map((qitem) => (
                  <TableRow key={qitem.id}>
                    <TableCell sx={{ color: UI.text }}>{qitem.promoId || "—"}</TableCell>
                    <TableCell sx={{ color: UI.text }}>
                      {qitem.submittedAt ? new Date(qitem.submittedAt).toLocaleString() : "—"}
                    </TableCell>
                    <TableCell sx={{ color: UI.text }}>
                      {Array.isArray(qitem.reasons) ? qitem.reasons.join(", ") : "—"}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleModeration(qitem, "approved")}
                          sx={{ color: UI.ok, borderColor: "rgba(52,211,153,.35)" }}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleModeration(qitem, "rejected")}
                          sx={{ color: UI.danger, borderColor: "rgba(251,113,133,.35)" }}
                        >
                          Reject
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {!loadingQueue && queue.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ color: UI.subtext }}>
                      Queue is empty
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </TabPanel>

      <Box sx={{ mt: 2, color: "rgba(148,163,184,.8)", fontSize: 12 }}>
        Logged in as: <b>{user?.email || user?.uid}</b>
      </Box>
    </Box>
  );
}

/**
 * Мини-виджет, чтобы быстро удалить stream по ID.
 * (оставил минимально, потому что твой старый список streams я убрал вместе с формой)
 */
function DeleteStreamQuick({ onDelete, UI }) {
  const [id, setId] = useState("");
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <input
        value={id}
        onChange={(e) => setId(e.target.value)}
        placeholder="avatar_streams docId"
        style={{
          flex: 1,
          height: 38,
          borderRadius: 10,
          border: "1px solid rgba(255,255,255,.14)",
          background: "rgba(2,6,23,.28)",
          color: UI.text,
          padding: "0 12px",
          outline: "none",
        }}
      />
      <Tooltip title="Delete Firestore doc">
        <IconButton
          onClick={() => id.trim() && onDelete(id.trim())}
          sx={{
            color: "rgba(229,231,235,.85)",
            border: "1px solid rgba(255,255,255,.10)",
            background: "rgba(2,6,23,.25)",
            "&:hover": { background: "rgba(251,113,133,.10)", borderColor: "rgba(251,113,133,.35)" },
          }}
          size="small"
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
